import {useEffect, useCallback, useRef} from "react"
import {QueryResult} from "@apollo/react-common"
import {useLazyQuery, QueryHookOptions, QueryLazyOptions} from "@apollo/react-hooks"
import {ApolloLoad, DocumentNode, ServiceOptions, deriveHookNameAndRenameFunctionForDevtools} from "./helpers/hookShared"
import API from "@irati/utils/services/API/utils/errorHandler/register"

type UseLazyQueryResult<A, B> = [
  (options?: Pick<QueryLazyOptions<B>, "variables">) => void,
  Prettify<
    Pick<QueryResult<A, B>, "loading" | "networkStatus" | "called" | "refetch" | "variables" | "fetchMore" | "updateQuery"> & {
      /** Data from the request */
      data: A | undefined
      /** An error content key from the failing service */
      error?: string
      /** A pending promise that can be passed to `<Loading />` components or collected in array.
       *
       * @example
       * function MyComp() {
       *   const [getReport, {load, data}] = useReportQuery()
       *
       *   return (
       *     <Button onClick={getReport}>Download report</Button>
       *     <Loading load={load}>
       *       {data}
       *     </Loading>
       *   )
       * }
       */
      load: ApolloLoad
    }
  >
]

/**
 * Stripped down variables to pass to useLazyQuery
 * More can be added if identified as needed, but minimsing the api surface for now
 */
type UseLazyQueryOptions<A, B> = Prettify<
  Pick<QueryHookOptions<A, B>, "fetchPolicy" | "pollInterval" | "partialRefetch" | "notifyOnNetworkStatusChange">
>

/**
 * Create a lazy query hook for reuse throughout the codebase. This is highly recomended over call `useLazyQuery` directly.
 *
 * The benifit of using this factory function is to get strongly typed values, and marshal the output into more commonly used formats,
 * not limited to but including a `load` value for passing to `Loading` components
 *
 * For details of the underlying `useLazyQuery` see [apollo client](https://www.apollographql.com/docs/react/api/react/hooks/#uselazyquery)
 *
 * @example
 * const useLazyUsersForBusiness = createUseLazyQuery<UserList, QueryUsersForBusinessArgs>(getUsersForBusiness, {
 *  key: "usersForBusiness"
 * })
 *
 * function Users({ userIds }) {
 *   const [fetchUsers, apollo] = useLazyUsersForBusiness({ userIds })
 *   return (</>
 *     <p>users<p>
 *     <button onClick={fetchUsers}>fetch</button>
 *     <Loading load={apollo.load} />
 *     <pre>{apollo.data}</pre>
 *   </>)
 * }
 */
export function createUseLazyQuery<A, B>(
  query: DocumentNode,
  {key, errorHandlers = [], ...useQueryOptions}: Prettify<ServiceOptions & UseLazyQueryOptions<A, B>>
) {
  errorHandlers.forEach((handler) => {
    API.add(handler)
  })

  function useHook(variables: B, options: UseLazyQueryOptions<A, B> = {}): UseLazyQueryResult<A, B> {
    type Refetch = QueryResult<A, B>["refetch"]

    const [queryFn, apollo] = useLazyQuery<A, B>(query, {variables, ...useQueryOptions, ...options})

    // Here we are overriding the error type to be a PB Error as we know this will be handled by our client
    const error = apollo.error as string | undefined

    // here we assert the key index will be of type `A` - don't assume it should be
    // `keyof A` because we often define types in terms of the nested graphql type, not the root type
    const data = key && apollo.data ? ((apollo.data as any)[key] as A) : apollo.data

    // START HACK:
    // apollo-hooks has some open issues for this, which are resolved in client v3, we are on v2
    // see https://github.com/apollographql/react-apollo/issues/3745 and https://github.com/apollographql/react-apollo/issues/3862
    //
    // tl;dr If calling `refetch` from an unmounted component, the underlying hook as cleaned up its state and no longer knows about the client
    // a valid use case is calling `refetch` in a timeout
    //
    // this keeps track of whether the hook is in a mounted component, and if not it will fall back to the global client
    // ideally we use the original `refetch` as this will set things like `loading`, `error` appropriately etc
    const mounted = useRef(false)

    useEffect(() => {
      mounted.current = true

      return () => {
        mounted.current = false
      }
    }, [])

    const {refetch: _refetch, client} = apollo

    const refetch: Refetch = useCallback(
      (arg) => {
        if (mounted.current) {
          return _refetch(arg)
        } else {
          return client.query({query, variables: arg ?? variables, fetchPolicy: "network-only"})
        }
      },
      [mounted, variables, _refetch, client]
    )
    // END HACK

    return [
      queryFn,
      {
        ...apollo,
        refetch,
        error,
        load: {
          ...apollo,
          error,
          __brand: "ApolloLoad"
        },
        data
      }
    ]
  }

  deriveHookNameAndRenameFunctionForDevtools(useHook, "Query", query)
  return useHook
}
